/**
 * riff-scrub.js — стереть метаданные AVI (RIFF), не перекладывая файл.
 *
 * Третий и последний контейнер того же приёма. У mp4 ненужная коробка
 * гасится в `free`, у Matroska элемент — в `Void`, здесь чанк — в `JUNK`.
 * Во всех трёх случаях длина не меняется ни на байт, поэтому медиаданные не
 * двигаются, индексы остаются верными, а выход собирается из ССЫЛОК
 * `file.slice()` — файл не проходит через кучу wasm.
 *
 * Почему это дёшево именно у RIFF: заголовок чанка фиксирован — четыре
 * байта имени и четыре байта длины (little-endian). Чтобы погасить чанк,
 * достаточно переписать имя на `JUNK` и обнулить тело; длина остаётся той
 * же, и следующий чанк начинается там же, где начинался.
 *
 * Что гасится
 * -----------
 * Все метаданные AVI живут в `LIST INFO` перед `LIST movi`: `INAM`
 * (название), `IART` (исполнитель), `ICMT` (комментарий), `ICOP`
 * (авторские права), `ISFT` (программа), `ICRD` (дата) и прочие. Точечное
 * гашение работает по этим четырёхбуквенным именам, полное — по всему
 * списку разом.
 *
 * ⛔ Только СТИРАНИЕ. Записать новое значение, не меняя длины, в общем
 * случае нельзя — правка тегов остаётся за ffmpeg.
 *
 * API
 * ---
 *   RiffScrub.looksSupported(file) -> bool
 *   RiffScrub.scrub(file, ops) -> Promise<{blob, wiped, bytesPatched}>
 *      ops: {tags, date, software, all} — те же имена, что у пульта.
 *
 * Бросает со стабильной причиной: not-riff, no-info, nothing-to-do,
 * short-read, bad-chunk.
 */
(function () {
'use strict';
if (window.RiffScrub) return;

var HEAD_BYTES = 8 * 1024 * 1024;   // метаданные лежат до `movi`, то есть в голове
var TOP_GUARD = 4096;

function err(reason) { var e = new Error(reason); e.riffReason = reason; return e; }

function looksSupported(file) {
    var n = String((file && file.name) || '').toLowerCase();
    var t = String((file && file.type) || '').toLowerCase();
    return /\.avi$/.test(n) || t === 'video/x-msvideo' || t === 'video/avi';
}

function slice(file, from, to) {
    return new Promise(function (resolve, reject) {
        var fr = new FileReader();
        fr.onload = function () { resolve(new Uint8Array(fr.result)); };
        fr.onerror = function () { reject(err('short-read')); };
        fr.readAsArrayBuffer(file.slice(from, to));
    });
}

function fourcc(buf, o) {
    return String.fromCharCode(buf[o], buf[o + 1], buf[o + 2], buf[o + 3]);
}
function u32le(buf, o) {
    return (buf[o] | (buf[o + 1] << 8) | (buf[o + 2] << 16)) + buf[o + 3] * 16777216;
}

/* Имена чанков INFO → что они раскрывают. Точечные пункты пульта гасят
   только своё; «всё» уносит весь список. */
var INFO_OPS = {
    tags:     ['INAM', 'IART', 'ICMT', 'ICOP', 'IPRD', 'ISBJ', 'IGNR', 'IKEY', 'IENG', 'ICMS'],
    date:     ['ICRD', 'IDIT'],
    software: ['ISFT', 'ITCH'],
    device:   ['IPRD', 'IMED']
};

/* Пройти чанки RIFF на одном уровне. `LIST` несёт ещё один fourcc типа. */
function walk(buf, from, to, cb) {
    var p = from, guard = 0;
    while (p + 8 <= to && guard++ < TOP_GUARD) {
        var id = fourcc(buf, p), size = u32le(buf, p + 4);
        if (size < 0 || p + 8 + size > to) {
            // Последний чанк бывает обрезан границей прочитанного куска —
            // это не ошибка файла, просто дальше не наш кусок.
            break;
        }
        var listType = (id === 'LIST' || id === 'RIFF') ? fourcc(buf, p + 8) : '';
        if (cb(id, listType, p, size) === false) return;
        p += 8 + size + (size & 1);      // чанки выравниваются на чётную границу
    }
}

/* Погасить чанк: имя → JUNK, тело → нули. Длина не меняется. */
function toJunk(buf, at, size, wiped, label) {
    buf[at] = 0x4A; buf[at + 1] = 0x55; buf[at + 2] = 0x4E; buf[at + 3] = 0x4B;  // J U N K
    buf.fill(0, at + 8, at + 8 + size);
    wiped.push({ what: label, at: at, bytes: size + 8 });
}

async function scrub(file, ops) {
    ops = ops || {};
    if (!looksSupported(file)) throw err('not-riff');

    var head = await slice(file, 0, Math.min(HEAD_BYTES, file.size));
    if (head.length < 12 || fourcc(head, 0) !== 'RIFF') throw err('not-riff');

    var wanted = {};
    var wantAll = !!ops.all;
    Object.keys(INFO_OPS).forEach(function (op) {
        if (wantAll || ops[op]) INFO_OPS[op].forEach(function (n) { wanted[n] = 1; });
    });

    var wiped = [], infoAt = -1, infoSize = 0;
    walk(head, 12, head.length, function (id, listType, at, size) {
        if (id === 'LIST' && listType === 'movi') return false;   // дальше только медиаданные
        if (id === 'LIST' && listType === 'INFO') { infoAt = at; infoSize = size; }
    });
    if (infoAt < 0) throw err('no-info');

    if (wantAll) {
        toJunk(head, infoAt, infoSize, wiped, 'LIST INFO');
    } else {
        walk(head, infoAt + 12, infoAt + 8 + infoSize, function (id, lt, at, size) {
            if (wanted[id]) toJunk(head, at, size, wiped, id);
        });
    }
    if (!wiped.length) throw err('nothing-to-do');

    var end = Math.min(head.length, infoAt + 8 + infoSize);
    var parts = [head.subarray(0, end)];
    if (end < file.size) parts.push(file.slice(end));

    return {
        blob: new Blob(parts, { type: file.type || 'video/x-msvideo' }),
        wiped: wiped,
        bytesPatched: wiped.reduce(function (a, w) { return a + w.bytes; }, 0)
    };
}

window.RiffScrub = { looksSupported: looksSupported, scrub: scrub };
})();
